import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PasswordField } from "@/components/ui/PasswordField";
import { useUpdateSetting, type AdminSetting } from "@/queries/adminSettings";

interface ReplaceSecretDialogProps {
  setting: AdminSetting;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

/**
 * Captures a new plaintext value for a secret setting and PATCHes it to
 * the server, which pgcrypto-encrypts before storage. The current value
 * is never displayed.
 */
export function ReplaceSecretDialog({
  setting,
  open,
  onOpenChange,
}: ReplaceSecretDialogProps) {
  const { t } = useTranslation("admin-settings");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateSetting(setting.key);

  const reset = () => {
    setValue("");
    setError(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (trimmed === "") {
      setError(t("errors.required"));
      return;
    }
    mutation.mutate(
      { value: trimmed },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
        onError: () => setError(t("errors.saveGeneric")),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t("replaceSecret.title", { key: setting.key })}</DialogTitle>
            <DialogDescription>{t("replaceSecret.description")}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <Label htmlFor={`secret-${setting.key}`}>
              {t("replaceSecret.newValueLabel")}
            </Label>
            <PasswordField
              id={`secret-${setting.key}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="new-password"
              disabled={mutation.isPending}
              data-testid="replace-secret-input"
            />
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t("replaceSecret.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || value.trim() === ""}
              data-testid="replace-secret-submit"
            >
              {mutation.isPending
                ? t("replaceSecret.submitting")
                : t("replaceSecret.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
